import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { TbPlus, TbEdit, TbTrash } from 'react-icons/tb';
import AddStageModal from './AddStageModal';
import EditStageModal from './EditStageModal';
import DeleteStageModal from './DeleteStageModal';

const AddPipelineModal = ({ show, onHide }) => {
  const [pipelineName, setPipelineName] = useState('');
  const [stages, setStages] = useState([
    { id: 1, name: 'Inpipeline' },
    { id: 2, name: 'Follow Up' },
    { id: 3, name: 'Schedule Service' },
  ]);
  const [access, setAccess] = useState('all');

  // Stage Modal States
  const [showAddStage, setShowAddStage] = useState(false);
  const [showEditStage, setShowEditStage] = useState(false);
  const [showDeleteStage, setShowDeleteStage] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [deletingStage, setDeletingStage] = useState(null);

  // Close main modal when opening stage modal
  const openAddStage = () => {
    onHide();           // Close main pipeline modal
    setShowAddStage(true);
  };

  const openEditStage = (stage) => {
    onHide();           // Close main pipeline modal
    setEditingStage(stage);
    setShowEditStage(true);
  };

  const openDeleteStage = (stage) => {
    onHide();           // Close main pipeline modal
    setDeletingStage(stage);
    setShowDeleteStage(true);
  };

  // Re-open main modal after closing stage modals
  const handleStageModalClose = () => {
    setShowAddStage(false);
    setShowEditStage(false);
    setShowDeleteStage(false);
    setEditingStage(null);
    setDeletingStage(null);
    
    // Re-open main modal after small delay for smooth UX
    setTimeout(() => {
      // You can call a callback from parent if needed, or just let user click Add Pipeline again
    }, 300);
  };

  const addNewStage = (name) => {
    setStages([...stages, { id: Date.now(), name }]);
  };

  const updateStage = (id, newName) => {
    setStages(stages.map(stage =>
      stage.id === id ? { ...stage, name: newName } : stage
    ));
  };

  const removeStage = (id) => {
    setStages(stages.filter(stage => stage.id !== id));
  };

  return (
    <>
      {/* ===================== MAIN PIPELINE MODAL ===================== */}
      <Modal 
        show={show} 
        onHide={onHide} 
        centered 
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-semibold fs-4">Add New Pipeline</Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-3 px-4">
          <Form>
            <Form.Group className="mb-4">
              <Form.Label className="fw-medium">
                Pipeline Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter pipeline name"
                value={pipelineName}
                onChange={(e) => setPipelineName(e.target.value)}
                className="py-3 rounded-3"
              />
            </Form.Group>

            {/* Pipeline Stages */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Form.Label className="fw-medium mb-0">
                  Pipeline Stages <span className="text-danger">*</span>
                </Form.Label>
                <Button style={{ color: "#ff6b1a"}}
                  variant=""
                  className="text-orange fw-medium p-0 d-flex align-items-center"
                  onClick={openAddStage}
                >
                  <TbPlus size={20} className="me-1" />
                  Add New
                </Button>
              </div>

              {stages.map((stage) => (
                <div
                  key={stage.id}
                  className="d-flex align-items-center gap-3 mb-3 p-3 bg-light border rounded-3"
                >
                  <div className="text-muted fs-5">⋮⋮</div>
                  <Form.Control
                    type="text"
                    value={stage.name}
                    readOnly
                    className="flex-grow-1 border-0 bg-transparent shadow-none py-2"
                  />
                  <div className="d-flex gap-2">
                    <Button
                      variant="light"
                      size="sm"
                      className="p-2"
                      onClick={() => openEditStage(stage)}
                    >
                      <TbEdit size={18} />
                    </Button>
                    <Button
                      variant="light"
                      size="sm"
                      className="p-2 text-danger"
                      onClick={() => openDeleteStage(stage)}
                    >
                      <TbTrash size={18} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Access */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-medium">Access</Form.Label>
              <div className="d-flex gap-4 mt-2">
                <Form.Check
                  type="radio"
                  label="All"
                  checked={access === 'all'}
                  onChange={() => setAccess('all')}
                />
                <Form.Check
                  type="radio"
                  label="Select Person"
                  checked={access === 'select'}
                  onChange={() => setAccess('select')}
                />
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-2 pb-4 px-4">
          <Button variant="light" className="px-4" onClick={onHide}>
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: '#FF6B00', borderColor: '#FF6B00' }}
            className="px-4"
            onClick={onHide}
          >
            Add Pipeline
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===================== STAGE MODALS ===================== */}
      <AddStageModal
        show={showAddStage}
        onHide={handleStageModalClose}
        onAdd={addNewStage}
      />

      <EditStageModal
        show={showEditStage}
        onHide={handleStageModalClose}
        currentName={editingStage?.name}
        onSave={(newName) => updateStage(editingStage?.id, newName)}
      />

      <DeleteStageModal
        show={showDeleteStage}
        onHide={handleStageModalClose}
        stageName={deletingStage?.name}
        onConfirm={() => {
          removeStage(deletingStage?.id);
          handleStageModalClose();
        }}
      />
    </>
  );
};

export default AddPipelineModal;